import { Injectable } from '@angular/core';

import { BaseService, Logger } from 'ngx-elvis-ds';

import { HttpClient } from '@angular/common/http';
import { DeviceType } from './base/device-type.enum';
import { Declarations } from './base/declarations';
import { ICurrencyInfo } from './services/i18n.service';
import { Theme } from 'ngx-elvis-ds/lib/theme';

@Injectable({
  providedIn: 'root'
})
export class ConfigService extends BaseService {

  deviceTypes = DeviceType;

  private filename = 'assets/config.json';
  loaded = false;

  theme: string;
  cloudPOS: boolean = false;
  
  isLogged = false;
  loginSent = false;

  samSettings: { address: string } = { address: `${window.location.hostname}:${window.location.protocol === 'https:' ? 2445 : 2005}` };
  assetsSettings?: { address?: string } = { address: window.location.origin };

  // i18n
  language: number = 44;
  currency: ICurrencyInfo = {
    symbol: '€',
    description: 'Euro',
    name: 'EUR',
    divider: 100,
    digitsInfo: '2.2-2'
  };

  storeSettings?: {
    operator?: string,
    operatorPassword?: string,
    storeId?: number,
    deviceId?: number,
    deviceType?: number,
  } = {
  };

  guiSettings?: {
    leftHandUse?: boolean,
    enableSound?: boolean,
    enableSpeech?: boolean,
    welcome?: { mode: string },
    selfPOSMonitor?: boolean,
    gloryStatusMonitor?: boolean,
    disableAsyncSearch?: boolean,
    searchDebounceTime?: number,
    enableVelocityCodes?: boolean,
    disableDeclarations?: boolean,
    scanditLicenseKey?: string,
    displayBestSellers?: boolean,
    displayXactInfo?: boolean,
    disableCameraScanner?: boolean,
    disableCustomerSearch?: boolean,
    disableItemsSearch?: boolean,
    disableCalculator?: boolean,
    hideFidelityRequest?: boolean,
    trainingMode?: boolean,
    advancedMode?: boolean,
    autoLogin?: boolean,
    loginMode?: string,
    enableDragScrollFix?: boolean,
    enableRecovery?: boolean,
    themeColors?: {
      primary: string;
      secondary: string;
      tertiary: string;
      success: string;
      warning: string;
      danger: string;
      dark: string;
      medium: string;
      light: string;
    },
  } = {
    advancedMode: true,
    leftHandUse: false,
    enableSound: true,
    enableSpeech: true,
    selfPOSMonitor: false,
    gloryStatusMonitor: false,
    disableAsyncSearch: true,
    disableDeclarations: false,
    welcome: { mode: 'static' },
    displayBestSellers: false,
    displayXactInfo: true,
    disableCameraScanner: false,
    disableCustomerSearch: false,
    disableItemsSearch: false,
    disableCalculator: false,
    hideFidelityRequest: false,
    trainingMode: false,
    loginMode: 'default',
    enableDragScrollFix: false,
    enableRecovery: false,
  };

  cartSettings?: {
    followUpItem?: {
      code: string,
      kitchen: number
    }
  } = { };

  paymentSettings?: {
    paymentDeclarations?: any[]
  } = {
    paymentDeclarations: Declarations.EUR
  };

  hardwareSettings: {
    cash?: any,
    eft?: any
  } = {};

  cfgParams: { key: string, value: string }[] = [];

  cfgDeviceId: any = null;
  cfgFileName: any = null;

  get samAddress() {
    if (this.samSettings && this.samSettings.address) {
      return this.samSettings.address;
    } else {
      return window.location.host;
    }
  }

  set samAddress(address: string) {
    this.samSettings.address = address;
  }

  get assetsBase() {
    if (!this.assetsSettings.address) {
      return window.location.origin;
    } else {
      return this.assetsSettings.address;
    }
  }

  get deviceType() {
    return this.storeSettings.deviceType || DeviceType.POS;
  }

  set deviceType(deviceType: number) {
    this.storeSettings.deviceType = deviceType;
  }

  get loginMode() {
    try {
      return this.guiSettings.loginMode;
    } catch (error) {
      return 'default';
    }
  }

  constructor(
    private http: HttpClient
  ) {
    super();
  }

  async load() {
    return new Promise((resolve, reject) => {
      this.http.get(this.filename).subscribe(
        (config) => {
          this.applyConfig(config);
          this.events.complete();
          this.loaded = true;
          resolve(true);
        },
        (err) => {
          Logger.error('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
          Logger.error('!! NO CONFIGURATION FILE FOUND !!');
          Logger.error(err.message);
          Logger.error('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!');
          this.events.complete();
          this.loaded = true;
          resolve(true);
        }
      );
    });
  }

  async set(config: any) {
    this.applyConfig(config);
    this.events.complete();
    this.loaded = true;
  }

  async remoteLookup(query: string, host?: string) {
    try {
      host = new URL(`http://${this.samAddress}`).hostname;
    } catch (error) {
      
    }

    let remoteLookupURL = `${window.location.protocol}//`;
    let port = 7392;

    if (window.location.protocol === 'https:') {
      port = 7492;
    }

    if (host) {
      remoteLookupURL += `${host}:${port}/api/db-operations/remote-lookup`;
    } else {
      // remoteLookupURL += `alpha.elvispos.com:${port}/api/db-operations/remote-lookup`;
      remoteLookupURL += `${window.location.hostname}:${port}/api/db-operations/remote-lookup`
    }

    const body = { request: { query: query, command: 1003 } };

    const resp = await this.http.post(remoteLookupURL, body, {responseType: 'text'}).toPromise();

    return resp;
  }

  // private

  private applyConfig(config: any) {
    if (config.theme) {
      this.theme = config.theme;
    }
    if (config.language) {
      this.language = config.language;
    }
    if (config.cloudPOS) {
      this.cloudPOS = config.cloudPOS;
    }

    // patch for backward compatibility
    if (config.storeSettings && config.storeSettings.advancedMode !== undefined) {
      if (!config.guiSettings) {
        config.guiSettings = {}
      }
      if (config.guiSettings.advancedMode === undefined) {
        config.guiSettings.advancedMode = config.storeSettings.advancedMode;
      }
    }

    Object.assign(this.samSettings, config.samSettings);
    Object.assign(this.assetsSettings, config.assetsSettings);
    Object.assign(this.storeSettings, config.storeSettings);
    Object.assign(this.guiSettings, config.guiSettings);
    Object.assign(this.cartSettings, config.cartSettings);
    Object.assign(this.paymentSettings, config.paymentSettings);
    Object.assign(this.hardwareSettings, config.hardwareSettings);

    // if (window.location.protocol !== 'https:') {
    //   this.guiSettings.disableCameraScanner = true;
    // }
  }
}
